import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, Settings } from 'lucide-react';

/**
 * Menu « Admin » de la barre de navigation (synotis-support#4), même
 * organisation que synolog : un bouton qui déroule les écrans d'administration
 * groupés par thème (`groups` = `[{ label, items: [{ key, label, Icon }] }]`).
 *
 * Le panneau est en `position: fixed` (calée sur le bouton) : la nav passe en
 * défilement horizontal sur mobile et couperait un panneau en `absolute`.
 * Fermeture au clic extérieur, à Échap et après le choix d'un écran.
 * `badges` : compteur par clé d'écran (ex. comptes en attente).
 */
export default function AdminMenu({ groups, activeKey, onNavigate, badges = {}, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const items = groups.flatMap((g) => g.items);
  const active = items.some((i) => i.key === activeKey);
  const total = Object.values(badges).reduce((s, n) => s + (n || 0), 0);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;
    const place = () => {
      const r = buttonRef.current.getBoundingClientRect();
      const width = 260;
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (key) => {
    setOpen(false);
    onNavigate(key);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.5rem 1rem',
          borderRadius: 'var(--radius-pill)',
          border: '1px solid var(--glass-border)',
          background: active ? 'var(--primary)' : 'var(--glass)',
          color: active ? '#fff' : 'var(--text)',
          cursor: 'pointer',
          fontSize: '0.9rem',
          fontWeight: active ? 600 : 400,
        }}
      >
        <Settings size={16} />
        Admin
        {total > 0 && <CountBadge n={total} />}
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          aria-label="Administration"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: 260,
            maxHeight: 'calc(100vh - 140px)',
            overflowY: 'auto',
            zIndex: 200,
            background: 'var(--card-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            padding: '0.4rem',
          }}
        >
          {groups.map((g, gi) => (
            <div key={g.label} role="group" aria-label={g.label} style={{ paddingTop: gi ? '0.35rem' : 0, borderTop: gi ? '1px solid var(--glass-border)' : 'none', marginTop: gi ? '0.35rem' : 0 }}>
              <div style={{ padding: '0.35rem 0.6rem 0.25rem', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
                {g.label}
              </div>
              {g.items.map(({ key, label, Icon }) => {
                const current = key === activeKey;
                return (
                  <button
                    key={key}
                    type="button"
                    role="menuitem"
                    aria-current={current ? 'page' : undefined}
                    onClick={() => choose(key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.55rem',
                      width: '100%',
                      padding: '0.45rem 0.6rem',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      background: current ? 'var(--primary)' : 'transparent',
                      color: current ? '#fff' : 'var(--text)',
                      cursor: 'pointer',
                      fontSize: '0.88rem',
                      fontWeight: current ? 600 : 400,
                      textAlign: 'left',
                    }}
                  >
                    <Icon size={15} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{label}</span>
                    {badges[key] > 0 && <CountBadge n={badges[key]} />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function CountBadge({ n }) {
  return (
    <span
      title={`${n} compte(s) en attente d'activation`}
      style={{
        padding: '0.1rem 0.45rem',
        borderRadius: 999,
        fontSize: '0.7rem',
        fontWeight: 600,
        background: 'var(--warning)',
        color: '#fff',
        lineHeight: 1.2,
      }}
    >
      {n}
    </span>
  );
}
